import React, { useState, useEffect } from 'react';
import { fetchPosts } from '../../services/postService';
import PostWithMap from './PostWithMap';
import MultiMarkerMap from '../map/MultiMarkerMap';
import Loader from '../common/Loader';
import ErrorMessage from '../common/ErrorMessage';
import VirtualFeed from './VirtualFeed';
import { useAuth } from '../../contexts/AuthContext';
import { FaMapMarkedAlt, FaList } from 'react-icons/fa';
import smartImagePreloader from '../../utils/smartImagePreloader';
import './Feed.css';

/**
 * Feed component displays a list of posts with maps in a Wanderlog style
 */
const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Fetch initial posts
  useEffect(() => {
    const loadPosts = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPosts(1, 10, user.id);
        setPosts(data);
        setHasMore(data.length >= 10); // Assuming 10 is the page size
        
        // Initialize smart image preloader with all posts
        if (data.length > 0) {
          smartImagePreloader.initialize(data);
          
          // Log performance stats after a delay
          setTimeout(() => {
            const stats = smartImagePreloader.getStats();
            console.log('📊 Smart Preloader Stats:', stats);
          }, 2000);
        }
      } catch (err) {
        setError('Failed to load posts. Please try again later.');
        console.error('Error fetching posts:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [user]);

  // Handle loading more posts when user clicks "Load More"
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const newPosts = await fetchPosts(nextPage, 10, user.id);
      
      if (newPosts.length > 0) {
        setPosts(prevPosts => [...prevPosts, ...newPosts]);
        setPage(nextPage);
        setHasMore(newPosts.length >= 10); // Assuming 10 is the page size
        
        // Add new posts to smart preloader
        smartImagePreloader.addPosts(newPosts);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setError('Failed to load more posts. Please try again.');
      console.error('Error fetching more posts:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle adding a new post to the feed
  const handleNewPost = (newPost) => {
    setPosts(prevPosts => [newPost, ...prevPosts]);
  };

  // Handle updating a post in the feed
  const handlePostUpdate = (updatedPost) => {
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post.id === updatedPost.id ? updatedPost : post
      )
    );
  };

  // Handle post deletion
  const handlePostDelete = (postId) => {
    setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  };

  // Toggle between list and map view
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'list' ? 'map' : 'list');
    // Clear selected location when switching views
    setSelectedLocationId(null);
  };

  // Handle location click in the post
  const handleLocationClick = (location) => {
    if (viewMode === 'list') {
      // Switch to map view when a location is clicked in list view
      setViewMode('map');
      setSelectedLocationId(location.id);
    } else {
      // In map view, update the selected location and center map on the pin
      setSelectedLocationId(location.id);
      
      // Find the corresponding location data for the map
      const locationData = getLocationData().find(loc => loc.id === location.id);
      if (locationData) {
        // Trigger the marker click to center the map
        handleMarkerClick(locationData);
      }
    }
  };

  // Get all valid location data from posts
  const getLocationData = () => {
    // Add console log to debug post locations
    console.log("Posts with place data:", posts.map(post => ({
      id: post.id,
      has_place: !!post.place,
      place_details: post.place ? {
        id: post.place.id,
        name: post.place.name,
        lat: post.place.lat,
        lng: post.place.lng
      } : 'No place data'
    })));
    
    return posts
      .filter(post => post.place && post.place.lat && post.place.lng)
      .map(post => ({
        id: post.place.id,
        name: post.place.name,
        lat: post.place.lat,
        lng: post.place.lng,
        postId: post.id
      }));
  };

  // Handle map marker click - find the corresponding post and scroll to it
  const handleMarkerClick = (location) => {
    setSelectedLocationId(location.id);
    
    // Find the corresponding post
    const targetPost = posts.find(post => post.place?.id === location.id);
    if (targetPost) {
      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        const postElement = document.getElementById(`post-${targetPost.id}`);
        if (postElement) {
          // Add temporary highlight effect
          postElement.classList.add('highlight-post');
          
          // Scroll to the post
          postElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Remove highlight after animation
          setTimeout(() => {
            postElement.classList.remove('highlight-post');
          }, 2000);
        }
      }, 100);
    }
  };

  if (!user && !loading) {
    return (
      <div className="feed-container empty-feed-message">
        <p>Please log in to see your feed.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feed-container loading-container">
        <Loader size="large" />
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="feed-container error-container">
        <ErrorMessage message={error} />
        <button 
          className="retry-button"
          onClick={() => {
            setPage(1);
            setLoading(true);
            setPosts([]);
            // Re-trigger the initial useEffect
            const loadPosts = async () => {
              try {
                const data = await fetchPosts(1, 10, user.id);
                setPosts(data);
                setHasMore(data.length >= 10);
                setError(null);
              } catch (err) {
                setError('Failed to load posts. Please try again later.');
              } finally {
                setLoading(false);
              }
            };
            loadPosts();
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  const locationData = getLocationData();

  return (
    <div className="feed-container">
      <div className="feed-header">
        <h2>Your Feed</h2>
        <div className="view-toggle">
          <button 
            className={`view-toggle-button ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            aria-label="List View"
          >
            <FaList />
          </button>
          <button 
            className={`view-toggle-button ${viewMode === 'map' ? 'active' : ''}`}
            onClick={() => setViewMode('map')}
            aria-label="Map View"
            disabled={locationData.length === 0}
          >
            <FaMapMarkedAlt />
          </button>
        </div>
      </div>
      
      {posts.length === 0 && !loading ? (
        <div className="empty-feed-message">
          <p>Your feed is empty!</p>
          <p>Follow some users or create your first post to get started.</p>
        </div>
      ) : viewMode === 'map' && locationData.length > 0 ? (
        // Map view
        <div className="feed-map-view">
          <div className="consolidated-map">
            <MultiMarkerMap 
              locations={locationData}
              onMarkerClick={handleMarkerClick}
              selectedLocationId={selectedLocationId}
            />
          </div>
          <div className="map-posts-list">
            {posts.map(post => (
              <div 
                key={post.id} 
                id={`post-${post.id}`} 
                className={`map-post-item ${selectedLocationId && post.place?.id === selectedLocationId ? 'selected-post' : ''}`}
              >
                <PostWithMap
                  post={post}
                  onPostUpdate={handlePostUpdate}
                  onPostDelete={handlePostDelete}
                  onLocationClick={() => handleLocationClick(post.place)}
                  feedViewMode={viewMode}
                  isSelected={selectedLocationId && post.place?.id === selectedLocationId}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        // List view with virtual scrolling for better performance
        <>
          {posts.length > 10 ? (
            // Use virtual scrolling for large feeds
            <VirtualFeed
              posts={posts}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loading={loadingMore}
            />
          ) : (
            // Use regular rendering for small feeds
            <div className="posts-list snap-feed-container">
              {posts.map(post => (
                <div key={post.id} id={`post-${post.id}`} className="snap-feed-item">
                  <PostWithMap
                    post={post}
                    onPostUpdate={handlePostUpdate}
                    onPostDelete={handlePostDelete}
                    onLocationClick={post.place ? () => handleLocationClick(post.place) : null}
                    feedViewMode={viewMode}
                  />
                </div>
              ))}
            </div>
          )}
          
          {error && (
            <div className="load-more-error">
              <ErrorMessage message={error} />
            </div>
          )}
          
          {!hasMore && posts.length > 0 && (
            <div className="end-of-feed-message">
              <p>You've reached the end of your feed</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Feed; 
